import {ChangeDetectionStrategy, Component, computed, inject, signal, Type} from '@angular/core';
import {BZ_DIALOG_DATA, BzDialogRef, BzSelectOption} from '@bringoz/ui';
import {bzIconAccount, bzIconChevronDown, bzIconCompany, bzIconLocationPinOutline, bzIconRisk} from '@bringoz/ui/icons';
import {TranslateService} from '@ngx-translate/core';
import * as _ from 'lodash';
import * as moment from 'moment-timezone';
import {Observable} from 'rxjs';
import {PanelService} from '../../../../commons/angular/services/panel.service';
import {injectLegacy} from '../../../../commons/angular/utils/inject-legacy.util';
import type {SplitService} from '../../../../commons/services/split-service.service';
import type {
  AgentConfigurationProfilesRequestsService
} from '../../../../configuration-profiles/services/agent-configuration-profiles-requests.service';
import {BreakType} from '../../../../site-preferences/models/break-type.model';
import {AccountModel} from '../../../../account/angular/models/account.model';
import {Address} from '../../../../addresses/models/address.model';
import {ServicePointModel} from '../../../../service-points/angular/models/service-point.model';
import {getServicePointLabel} from '../../../../service-points/angular/utils/service-point-display.util';
import {TimeCardEntryLocationSourceType} from '../../../types/timecard-entry-location-source-type.type';
import {
  AddStopDialogData,
  AddStopDialogResult,
  TimeCardEntryType,
  TimecardOption
} from '../../../types/timekeeping-dialog.type';
import {
  buildCustomStopLocation,
  buildStopLocation,
  durationToMillis,
  EMPTY_STOP_LOCATION,
  isDuplicateBreakType,
  validateBreakOverlap,
  validateStopTimes
} from '../../utils/timekeeping-dialog.util';
import {TimecardAccountSelectPanelComponent} from '../timecard-account-select-panel/timecard-account-select-panel.component';
import {
  TimecardServicePointSelectPanelComponent
} from '../timecard-service-point-select-panel/timecard-service-point-select-panel.component';
import {TimecardSelectPanelData} from '../timecard-search-select-list/timecard-search-select-list.component';

interface FormState {
  canSubmit: boolean;
  error: string | null;
}

const HOUR_MS = 3600000;

const STOP_TYPES: { value: TimeCardEntryType; label: string }[] = [
  {value: TimeCardEntryType.Task, label: 'Task'},
  {value: TimeCardEntryType.CustomTask, label: 'Custom Task'},
  {value: TimeCardEntryType.AgentStartLocation, label: 'Start Location'},
  {value: TimeCardEntryType.AgentEndLocation, label: 'End Location'},
  {value: TimeCardEntryType.Break, label: 'Break'}
];

const LOCATION_SOURCES = [
  {type: TimeCardEntryLocationSourceType.ACCOUNT, icon: bzIconAccount, label: 'Account'},
  {type: TimeCardEntryLocationSourceType.SERVICE_POINT, icon: bzIconCompany, label: 'Service Location'},
  {type: TimeCardEntryLocationSourceType.CUSTOM, icon: bzIconLocationPinOutline, label: 'Custom Address'}
];

/** Stop types that take an arrival, a departure and a service time. */
const TASK_STOP_TYPES = [TimeCardEntryType.Task, TimeCardEntryType.CustomTask];

/** Allowed once per timecard. */
const SINGLE_STOP_TYPES = [TimeCardEntryType.AgentStartLocation, TimeCardEntryType.AgentEndLocation];

/**
 * Adds a stop to a timecard of the day: a task at an account, a custom task, a break, or the agent's
 * start/end location. A task at an account the timecard already has updates that entry's times instead.
 * A custom task takes its address from an account, a service point or a custom address, and the server
 * resolves the first two.
 */
require('../timekeeping-dialogs.scss');
@Component({
  standalone: false,
  selector: 'app-timecard-add-stop-dialog',
  templateUrl: './timecard-add-stop-dialog.html',
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class TimecardAddStopDialogComponent {

  readonly data = inject<AddStopDialogData>(BZ_DIALOG_DATA);
  private readonly ref = inject<BzDialogRef<AddStopDialogResult>>(BzDialogRef);
  private readonly translate = inject(TranslateService);
  private readonly panelService = inject(PanelService);
  private readonly splitService = injectLegacy<SplitService>('splitService');
  private readonly agentConfigurationProfilesRequestsService =
    injectLegacy<AgentConfigurationProfilesRequestsService>('agentConfigurationProfilesRequestsService');

  readonly StopType = TimeCardEntryType;
  readonly LocationSourceType = TimeCardEntryLocationSourceType;
  readonly locationSources = LOCATION_SOURCES;
  readonly icons = {chevron: bzIconChevronDown, warning: bzIconRisk};
  readonly timecards: TimecardOption[] = this.data.timecards || [];
  readonly timecardOptions: BzSelectOption<string>[] = this.timecards.map((timecard) => ({
    value: timecard.id,
    label: `${timecard.shiftName} (${this.formatTime(timecard.clockInTime)} - ${this.formatTime(timecard.clockOutTime)})`
  }));
  readonly hasAgentAddress = !!this.data.agentAddress?.location;

  readonly selectedTimecardId = signal<string>(this.data.selectedTimecardId || this.timecards[0]?.id);
  readonly stopType = signal<TimeCardEntryType>(TimeCardEntryType.Task);
  readonly referenceDate = signal<number>(this.data.referenceDate);
  readonly selectedAccount = signal<AccountModel | null>(null);
  readonly locationSourceType = signal<TimeCardEntryLocationSourceType>(TimeCardEntryLocationSourceType.ACCOUNT);
  readonly selectedServicePoint = signal<ServicePointModel | null>(null);
  readonly customAddress = signal<Address | null>(null);
  readonly arrivalTime = signal<number | null>(null);
  readonly departureTime = signal<number | null>(null);
  readonly serviceHours = signal<string>('0');
  readonly serviceMinutes = signal<string>('0');
  readonly breakTypes = signal<BreakType[]>([]);
  readonly isLoadingBreakTypes = signal(false);
  readonly selectedBreakTypeId = signal<string | null>(null);

  readonly selectedTimecard = computed(() => _.find(this.timecards, {id: this.selectedTimecardId()}));

  /** Start and End Location are allowed once per timecard; tasks and breaks have no limit. */
  readonly stopTypeOptions = computed<BzSelectOption<TimeCardEntryType>[]>(() => {
    const entryTypes = this.selectedTimecard()?.entryTypes || [];
    return STOP_TYPES.map(({value, label}) => ({
      value,
      label: this.translate.instant(label),
      disabled: _.includes(SINGLE_STOP_TYPES, value) && _.includes(entryTypes, value)
    }));
  });

  readonly breakTypeOptions = computed<BzSelectOption<string>[]>(() => this.breakTypes().map((breakType) => ({
    value: breakType.id,
    label: `${breakType.name} (${breakType.paid ? 'Paid' : 'Unpaid'})`
  })));

  readonly isTaskStop = computed(() => _.includes(TASK_STOP_TYPES, this.stopType()));

  /** Name shown on the account / service point trigger, empty while nothing is picked. */
  readonly locationSourceName = computed(() => this.locationSourceType() === TimeCardEntryLocationSourceType.SERVICE_POINT
    // Same label as in the list, which falls back to the address for a service point without a name
    ? this.selectedServicePoint()?.name || getServicePointLabel(this.selectedServicePoint())
    : this.selectedAccount()?.name);

  /**
   * Index among the timecard's active entries - the caller maps it back to the full list.
   * Only a Task updates an existing entry; a custom task at the same account is a stop of its own.
   */
  readonly existingEntryIndex = computed(() => {
    if (this.stopType() !== TimeCardEntryType.Task) {
      return null;
    }
    const accountId = this.selectedAccount()?.id;
    // The server sets accountId on a custom task at an account too - that one is not the task to update
    const index = accountId
      ? _.findIndex(this.selectedTimecard()?.entries,
        (entry) => entry.accountId === accountId && entry.type !== TimeCardEntryType.CustomTask)
      : -1;
    return index >= 0 ? index : null;
  });

  readonly duplicateBreakTypeWarning = computed(() => this.stopType() === TimeCardEntryType.Break
    && isDuplicateBreakType(this.selectedBreakTypeId(), this.selectedTimecard()));

  readonly formState = computed<FormState>(() => this.validate());

  constructor() {
    this.setDefaultTimes();
  }

  // ******************************
  //        Fields
  // ******************************

  onTimecardSelect(timecardId: unknown) {
    this.selectedTimecardId.set(timecardId as string);
    this.referenceDate.set(this.selectedTimecard()?.clockInTime || this.data.referenceDate);
    // Start/End Location may already exist in the newly selected timecard
    const currentOption = _.find(this.stopTypeOptions(), {value: this.stopType()});
    if (currentOption?.disabled) {
      this.stopType.set(TimeCardEntryType.Task);
    }
    this.prefillFromExistingEntry();
    this.setDefaultTimes();
  }

  onStopTypeChange(stopType: unknown) {
    this.stopType.set(stopType as TimeCardEntryType);
    // A task keeps the picked account, so a custom task picks up from it too
    if (_.includes(TASK_STOP_TYPES, stopType)) {
      this.setDefaultTimes();
      return;
    }

    this.selectedAccount.set(null);
    this.resetServiceTime();
    if (stopType === TimeCardEntryType.Break) {
      this.selectedBreakTypeId.set(null);
      this.loadBreakTypes();
      this.setDefaultTimes();
    } else {
      this.arrivalTime.set(null);
      this.departureTime.set(null);
    }
  }

  onLocationSourceChange(type: TimeCardEntryLocationSourceType) {
    this.locationSourceType.set(type);
  }

  /** Accounts, or service points when a custom task takes its address from one. */
  onOpenLocationPanel(event: MouseEvent) {
    // A Task always picks an account, whatever source was last chosen for a custom task
    const isServicePoint = this.stopType() === TimeCardEntryType.CustomTask
      && this.locationSourceType() === TimeCardEntryLocationSourceType.SERVICE_POINT;
    if (isServicePoint) {
      this.openSelectPanel(TimecardServicePointSelectPanelComponent, event, this.selectedServicePoint()?.id)
        .subscribe((servicePoint: ServicePointModel | null) => {
          if (servicePoint) {
            this.selectedServicePoint.set(servicePoint);
          }
        });
    } else {
      this.openSelectPanel(TimecardAccountSelectPanelComponent, event, this.selectedAccount()?.id)
        .subscribe((account: AccountModel | null) => {
          if (account) {
            this.selectedAccount.set(account);
            this.prefillFromExistingEntry();
          }
        });
    }
  }

  /** Emits what the panel was closed with: the picked item, or null when dismissed. */
  private openSelectPanel(component: Type<unknown>, event: MouseEvent, selectedId: string): Observable<unknown> {
    const data: TimecardSelectPanelData = {selectedId};
    return this.panelService.open(component, event, data, {
      position: {originX: 'start', originY: 'bottom', overlayX: 'start', overlayY: 'top', offsetY: 4}
    }).afterClose$;
  }

  /** Typing without picking a suggestion leaves an address with no coordinates, which cannot be submitted. */
  onCustomAddressChange(address: Address | undefined) {
    this.customAddress.set(address || null);
  }

  onBreakTypeSelect(breakTypeId: unknown) {
    this.selectedBreakTypeId.set(breakTypeId as string);
  }

  /** Pushes the departure an hour past a new arrival that reaches it, but not past clock-out. */
  onArrivalChange(time: number) {
    this.arrivalTime.set(time);
    const departure = this.departureTime();
    if (departure && time >= departure) {
      const clockOutTime = this.selectedTimecard()?.clockOutTime;
      const newDeparture = time + HOUR_MS;
      this.departureTime.set(clockOutTime && newDeparture > clockOutTime ? clockOutTime : newDeparture);
    }
  }

  onDepartureChange(time: number) {
    this.departureTime.set(time);
  }

  onCancel() {
    this.ref.close();
  }

  onSubmit() {
    if (!this.formState().canSubmit) {
      return;
    }
    this.ref.close(this.buildResult());
  }

  // ******************************
  //        Private
  // ******************************

  private validate(): FormState {
    const timecard = this.selectedTimecard();
    const hasTimecard = !this.timecards.length || !!timecard;
    const arrival = this.arrivalTime();
    const departure = this.departureTime();

    switch (this.stopType()) {
      case TimeCardEntryType.Task: {
        if (!this.selectedAccount() || !arrival || !departure || !hasTimecard) {
          return {canSubmit: false, error: null};
        }
        const error = validateStopTimes(arrival, departure, timecard?.clockInTime, timecard?.clockOutTime);
        return {canSubmit: !error, error};
      }
      case TimeCardEntryType.CustomTask: {
        if (!this.hasLocationSource() || !arrival || !departure || !hasTimecard) {
          return {canSubmit: false, error: null};
        }
        const error = validateStopTimes(arrival, departure, timecard?.clockInTime, timecard?.clockOutTime);
        return {canSubmit: !error, error};
      }
      case TimeCardEntryType.Break: {
        if (!hasTimecard) {
          return {canSubmit: false, error: null};
        }
        if (!this.selectedBreakTypeId()) {
          return {canSubmit: false, error: 'SELECT_BREAK_TYPE_REQUIRED'};
        }
        if (!arrival || !departure) {
          return {canSubmit: false, error: 'SELECT_BREAK_TIMES_REQUIRED'};
        }
        const error = validateStopTimes(arrival, departure, timecard?.clockInTime, timecard?.clockOutTime)
          || validateBreakOverlap(arrival, departure, timecard);
        return {canSubmit: !error, error};
      }
      default:
        return {canSubmit: hasTimecard, error: null};
    }
  }

  private buildResult(): AddStopDialogResult {
    const timecardId = this.selectedTimecardId();
    switch (this.stopType()) {
      case TimeCardEntryType.Task: {
        const account = this.selectedAccount();
        const serviceTime = durationToMillis(this.serviceHours(), this.serviceMinutes());
        return {
          accountId: account.id,
          accountName: account.name || '',
          arrival: this.arrivalTime(),
          departure: this.departureTime(),
          location: buildStopLocation(account.address, (account.address as any)?.formattedAddress),
          timecardId,
          isUpdateExisting: this.existingEntryIndex() !== null,
          existingEntryIndex: this.existingEntryIndex(),
          stopType: TimeCardEntryType.Task,
          serviceTime: serviceTime || undefined
        };
      }
      case TimeCardEntryType.CustomTask: {
        const serviceTime = durationToMillis(this.serviceHours(), this.serviceMinutes());
        const type = this.locationSourceType();
        const isCustom = type === TimeCardEntryLocationSourceType.CUSTOM;
        return {
          arrival: this.arrivalTime(),
          departure: this.departureTime(),
          // The server takes an account's or service point's address from the entity itself
          location: isCustom ? buildCustomStopLocation(this.customAddress()) : EMPTY_STOP_LOCATION,
          locationSource: {type, id: isCustom ? undefined : this.getLocationSourceId()},
          timecardId,
          isUpdateExisting: false,
          stopType: TimeCardEntryType.CustomTask,
          serviceTime: serviceTime || undefined
        };
      }
      case TimeCardEntryType.Break: {
        const breakType = _.find(this.breakTypes(), {id: this.selectedBreakTypeId()});
        return {
          location: EMPTY_STOP_LOCATION,
          timecardId,
          isUpdateExisting: false,
          stopType: TimeCardEntryType.Break,
          arrival: this.arrivalTime(),
          departure: this.departureTime(),
          breakTypeId: this.selectedBreakTypeId(),
          breakTypePaid: !!breakType?.paid
        };
      }
      default: {
        const address = this.hasAgentAddress ? this.data.agentAddress : null;
        return {
          location: buildStopLocation(address, address?.place),
          timecardId,
          isUpdateExisting: false,
          stopType: this.stopType()
        };
      }
    }
  }

  /** Clock-in and clock-out of the timecard, unless an existing entry's times are being edited. */
  private setDefaultTimes() {
    if (this.existingEntryIndex() !== null) {
      return;
    }
    const timecard = this.selectedTimecard();
    if (timecard?.clockInTime) {
      this.arrivalTime.set(timecard.clockInTime);
    }
    if (timecard?.clockOutTime) {
      this.departureTime.set(timecard.clockOutTime);
    }
  }

  private prefillFromExistingEntry() {
    const index = this.existingEntryIndex();
    const entry = index !== null ? this.selectedTimecard()?.entries[index] : null;
    if (!entry) {
      return;
    }
    if (!this.arrivalTime() && entry.startTime) {
      this.arrivalTime.set(entry.startTime);
    }
    if (!this.departureTime() && entry.endTime) {
      this.departureTime.set(entry.endTime);
    }
  }

  private hasLocationSource(): boolean {
    if (this.locationSourceType() === TimeCardEntryLocationSourceType.CUSTOM) {
      return !!this.customAddress()?.location;
    }
    return !!this.getLocationSourceId();
  }

  private getLocationSourceId(): string | undefined {
    return this.locationSourceType() === TimeCardEntryLocationSourceType.SERVICE_POINT
      ? this.selectedServicePoint()?.id
      : this.selectedAccount()?.id;
  }

  private resetServiceTime() {
    this.serviceHours.set('0');
    this.serviceMinutes.set('0');
  }

  /** Break types of the agent's configuration profile, fetched once. */
  private loadBreakTypes() {
    if (this.breakTypes().length || this.isLoadingBreakTypes() || !this.data.agentId) {
      return;
    }
    this.isLoadingBreakTypes.set(true);
    this.agentConfigurationProfilesRequestsService.getConfigurationByAgent(this.data.agentId)
      .then((profile: any) => this.agentConfigurationProfilesRequestsService.getBreakTypesForProfile(profile.id))
      .then((breakTypes: BreakType[]) => this.breakTypes.set(_.reject(breakTypes || [], 'archive')))
      .catch(() => this.breakTypes.set([]))
      .finally(() => this.isLoadingBreakTypes.set(false));
  }

  private formatTime(timestamp: number): string {
    if (!timestamp) {
      return '--:--';
    }
    const format = this.splitService.getDateAndTimeConfig().time || 'hh:mm A';
    return this.data.zoneId ? moment(timestamp).tz(this.data.zoneId).format(format) : moment(timestamp).format(format);
  }
}
